import React, { useCallback, useState } from 'react';
import { Button } from 'react-bootstrap';
import { analyticsTrack } from '../../web-analytics/webAnalytics';
import { AnalyticsEvent } from '../../web-analytics/AnalyticsEvent';
import { useCreateReferralLinkMutation } from '../hooks/useCreateReferralLinkMutation';
import { CopyIcon } from '../../assets/icons';
import { useNotification } from '../../notification/notificationsContext';
import { Customer } from '../types';
import { useLocalStorage } from '@rehooks/local-storage';
import { GrowegyCheckbox } from '../../program/components/GrowegyCheckbox';
import clsx from 'clsx';

const GetYourReferralLink = (props: { canHide: boolean; source: string }) => {
  const { canHide, source } = props;

  const [expanded, onExpandedChanged] = useLocalStorage<boolean>(
    'BillingPortalGetYourReferralLink/expanded',
    true
  );

  const { mutate: createReferralLinkMutation, status: createReferralLinkStatus } =
    useCreateReferralLinkMutation();

  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showError, setShowError] = useState(false);

  return expanded || !canHide ? (
    <div className="request-referral-link__container--link-is-not-requested">
      <div>
        Get your personal referral link and earn 10% on each new customer's first payment when you
        share Growegy.
      </div>
      <div>
        <GrowegyCheckbox
          defaultChecked={termsAccepted}
          dataTestPrefix="cancellation-intro__acknowledgement-checkbox"
          onChange={(e) => {
            const checked = e.target.checked;
            setTermsAccepted(checked);
            if (checked) setShowError(false);
          }}
        >
          <span>
            Accept{' '}
            <a
              href="https://www.growegy.com/growegy-affiliate-program-terms-of-service/"
              target="_blank"
              rel="noopener noreferrer"
            >
              Terms & Conditions
            </a>
          </span>
        </GrowegyCheckbox>
      </div>
      {showError && !termsAccepted && (
        <div className="growegy-font14--error">Please accept terms and conditions.</div>
      )}
      <div className="d-flex justify-content-between">
        <Button
          variant="primary"
          className="request-referral-link__apply-button"
          disabled={createReferralLinkStatus === 'loading'}
          onClick={() => {
            if (!termsAccepted) {
              setShowError(true);
              return;
            }
            createReferralLinkMutation();
          }}
        >
          {createReferralLinkStatus !== 'loading'
            ? 'Get referral link'
            : 'Creating referral link...'}
        </Button>
        {canHide && (
          <Button
            variant="light"
            className="upgrade-to-annual__hide-button"
            onClick={() => {
              onExpandedChanged(false);
              analyticsTrack(AnalyticsEvent.PERSONAL_REFERRAL_LINK_GET_COLLAPSED, {
                source,
              });
            }}
          >
            Hide
          </Button>
        )}
      </div>
    </div>
  ) : (
    <div>
      <Button
        variant="primary"
        className="request-referral-link__apply-button ml-auto"
        onClick={() => {
          onExpandedChanged(true);
          analyticsTrack(AnalyticsEvent.PERSONAL_REFERRAL_LINK_GET_EXPANDED, {
            source: 'banner',
          });
        }}
      >
        Get Referral Link
      </Button>
    </div>
  );
};

const ReferralLinkDetails = (props: { link: string; canHide: boolean; source: string }) => {
  const { link, canHide, source } = props;
  const { notifySuccess, notifyError } = useNotification();
  const [expanded, onExpandedChanged] = useLocalStorage<boolean>(
    'BillingPortalReferralLinkDetails/expanded',
    true
  );

  const onCopy = useCallback(async () => {
    try {
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/plain': new Blob([link], { type: 'text/plain' }),
        }),
      ]);
      analyticsTrack(AnalyticsEvent.PERSONA_REFERRAL_LINK_COPIED, {
        source,
      });
      notifySuccess({ notificationMsg: 'Copied to clipboard' });
    } catch (e) {
      notifyError({ notificationMsg: `Failed to copy to clipboard` });
    }
  }, [link, notifySuccess, notifyError, source]);

  return expanded || !canHide ? (
    <div className="referral-link-details__container">
      <div className="billing-flex-table__row--base">
        <div className="col-2 billing-table__column align-self-start growegy-title14--moderat">
          Referral link
        </div>
        <div className="col-10 billing-table__column--small-padding d-flex" onClick={onCopy}>
          <span className="growegy-label14--moderrat">{link}</span>
          <div style={{ marginLeft: '20px' }}>
            <CopyIcon />
          </div>
        </div>
      </div>
      <div
        className={clsx('d-flex flex-column billing-flex-table__row-max-width', {
          'growegy-title12': canHide,
        })}
      >
        <div>
          <span>
            Simply copy and share your unique referral link with businesses who could benefit from
            Growegy. Everyone who signs up and makes their first payment through your link earns you
            a 10% reward on the following billing cycle. For all the conditions, please review our{' '}
            <a
              href="https://www.growegy.com/growegy-affiliate-program-terms-of-service/"
              target="_blank"
              rel="noopener noreferrer"
            >
              Terms & Conditions
            </a>
          </span>
        </div>
      </div>
      {canHide && (
        <div className="d-flex">
          <div className="ml-auto">
            <Button
              variant="light"
              className="upgrade-to-annual__hide-button"
              onClick={() => {
                onExpandedChanged(false);
                analyticsTrack(AnalyticsEvent.PERSONAL_REFERRAL_LINK_COLLAPSED, {
                  source: 'banner',
                });
              }}
            >
              Hide
            </Button>
          </div>
        </div>
      )}
    </div>
  ) : (
    <div>
      <Button
        variant="primary"
        className="request-referral-link__apply-button ml-auto"
        onClick={() => {
          onExpandedChanged(true);
          analyticsTrack(AnalyticsEvent.PERSONAL_REFERRAL_LINK_EXPANDED, {
            source: 'banner',
          });
        }}
      >
        See Personal Referral Link
      </Button>
    </div>
  );
};

export const PersonalReferralLink = (props: {
  customer: Customer;
  canHide: boolean;
  source: string;
}) => {
  const { customer, canHide, source } = props;
  const referralLink = customer.referralLink;
  if (!referralLink) {
    return null;
  }

  if (!!referralLink.link) {
    return <ReferralLinkDetails link={referralLink.link} canHide={canHide} source={source} />;
  }

  return referralLink.wasRequested ? (
    <div className="request-referral-link__container--link-requested">
      Your referral link will appear here soon. Check back later!
    </div>
  ) : (
    <GetYourReferralLink canHide={canHide} source={source} />
  );
};
